import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  CheckCircle,
  Shield,
  FileCheck,
  ClipboardCheck,
  AlertTriangle,
  Building2,
  Heart,
  Users,
  TrendingUp,
  BookOpen,
  Search,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import MidPageCTA from "../components/MidPageCTA";
import SectionDivider from "../components/SectionDivider";

const ComplianceCredentialing = () => {
  const outcomes = [
    "Confidence that your practice is audit-ready at any time",
    "Up-to-date credentialing with all relevant payers and programs",
    "Organized documentation that meets regulatory standards",
    "Clear policies and procedures your team can actually follow",
    "Reduced risk of penalties, fines, or lost contracts",
    "Less time spent scrambling before inspections or renewals",
    "A repeatable system for staying compliant long-term",
  ];

  const whoIsFor = [
    { icon: Building2, label: "Private practices preparing for audits or renewals" },
    { icon: Heart, label: "Clinics needing help with Medicare or Medicaid credentialing" },
    { icon: Users, label: "Home healthcare agencies navigating regulatory requirements" },
    { icon: TrendingUp, label: "Healthcare startups setting up compliance from scratch" },
  ];

  return (
    <div>
      <SEO
        title="Healthcare Compliance & Credentialing Support for Clinics"
        description="Not sure if your clinic would pass an audit? RNB Consulting Group helps healthcare practices get compliant, stay organized, and prepare for credentialing and audits — without the stress."
        keywords="healthcare compliance help, clinic audit preparation, credentialing support medicare medicaid, healthcare licensing help, compliance consulting medical practice, audit readiness healthcare, regulatory compliance clinic"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.rnbconsultinggroup.com/services" },
          { "@type": "ListItem", "position": 3, "name": "Compliance & Credentialing", "item": "https://www.rnbconsultinggroup.com/services/compliance-credentialing" }
        ]
      }]} />

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-rnb-navy via-rnb-navy to-rnb-blue overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-rnb-teal/15 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-rnb-blue/20 rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8">
                <Shield className="w-4 h-4 text-rnb-teal" />
                <span className="text-sm font-medium">Healthcare Compliance & Credentialing</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6" data-testid="compliance-hero-headline">
                Stop Worrying About Whether
                <span className="block mt-2 bg-gradient-to-r from-rnb-teal to-cyan-300 bg-clip-text text-transparent">
                  You'd Pass an Audit Tomorrow
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-white/80 leading-relaxed mb-8">
                We help healthcare practices get compliant, stay organized, and prepare for credentialing and audits — so you can stop guessing and start running your practice with confidence.
              </p>

              <ul className="flex flex-wrap gap-x-6 gap-y-3 mb-10 text-white/90 text-sm sm:text-base">
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Audit preparation & readiness</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Credentialing support</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Policy & procedure development</li>
              </ul>

              <Link to="/consultation">
                <Button
                  size="lg"
                  className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-glow hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid="compliance-hero-cta"
                >
                  Schedule a Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="/images/healthcare/hospital-team-tablet.jpg"
                alt="Healthcare team reviewing compliance documentation on tablet"
                className="rounded-2xl shadow-2xl w-full h-56 sm:h-72 lg:h-auto object-cover border-2 border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Acknowledgment */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Does This Sound Like Your Practice?
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              Most practice owners we talk to are dealing with at least one of these compliance-related stressors.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              "You're not sure if your documentation would hold up under inspection",
              "Credentialing paperwork keeps piling up and deadlines feel impossible to track",
              "You've put off updating your policies and procedures for months (or years)",
              "You're not sure what regulations have changed or how they apply to you",
              "The idea of a surprise audit gives you a genuine pit in your stomach",
              "You're losing revenue because credentialing with payers is stalled or incomplete",
            ].map((problem, idx) => (
              <div key={problem.slice(0, 40)} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                </div>
                <p className="text-rnb-gray leading-relaxed">{problem}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              How We Help You Get and Stay Compliant
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              We don't overwhelm you with regulatory jargon. We roll up our sleeves and work through it with you, step by step.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Search, title: "Compliance Gap Assessment", desc: "We review your current documentation, workflows, and policies to identify exactly where the gaps are — and prioritize what to fix first." },
              { icon: FileCheck, title: "Credentialing & Re-Credentialing", desc: "We help you navigate the credentialing process with Medicare, Medicaid, and commercial payers — tracking deadlines, preparing applications, and following up." },
              { icon: ClipboardCheck, title: "Audit Preparation & Mock Inspections", desc: "We walk through your practice the way an auditor would, identify vulnerabilities, and help you address them before it matters." },
              { icon: BookOpen, title: "Policy & Procedure Development", desc: "We create or update your P&P manuals so they reflect current regulations and are written clearly enough for your team to actually use." },
              { icon: Shield, title: "Regulatory Implementation Support", desc: "When regulations change, we help you understand what's required and implement the operational changes to stay ahead of enforcement." },
              { icon: Building2, title: "Licensing & Documentation", desc: "We help organize and maintain the documentation you need for state licensing, accreditation, and payer contracts — so nothing falls through the cracks." },
            ].map((item, idx) => (
              <Card key={item.title || item.name || item.label || idx} className="border-0 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-7">
                  <div className="w-12 h-12 bg-rnb-light rounded-xl flex items-center justify-center mb-5">
                    <item.icon className="w-6 h-6 text-rnb-blue" />
                  </div>
                  <h3 className="text-lg font-bold text-rnb-navy mb-2">{item.title}</h3>
                  <p className="text-rnb-gray text-sm leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-page CTA */}
      <MidPageCTA variant={2} image="/images/healthcare/medical-team-meeting.png" />

      {/* Outcomes */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              What You Can Expect
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              Our goal is simple: help you get to a place where compliance feels manageable, not terrifying.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {outcomes.map((outcome, idx) => (
              <div key={outcome.slice(0, 40)} className="flex items-start gap-3 p-4">
                <CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0 mt-0.5" />
                <span className="text-rnb-navy font-medium">{outcome}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Who This Is For
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {whoIsFor.map((item, idx) => (
              <div key={item.title || item.name || item.label || idx} className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm">
                <div className="w-11 h-11 bg-rnb-light rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-rnb-blue" />
                </div>
                <span className="text-rnb-navy font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why RNB */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Why Healthcare Practices Choose RNB
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: Heart, title: "Healthcare-Only Expertise", desc: "We live and breathe healthcare compliance. We understand the regulatory landscape your practice operates in — HIPAA, OSHA, CMS, state-specific requirements, and more." },
              { icon: ClipboardCheck, title: "Hands-On Implementation", desc: "We don't just tell you what's wrong. We fix it with you — building the documentation, workflows, and systems your practice needs to stay compliant." },
              { icon: Shield, title: "Ongoing Support", desc: "Compliance isn't a one-time project. We help you build sustainable systems and offer ongoing support to keep you ahead of regulatory changes." },
              { icon: TrendingUp, title: "Scalable for Growth", desc: "Whether you're adding providers, opening new locations, or expanding services — we make sure your compliance infrastructure keeps up." },
            ].map((item, idx) => (
              <div key={item.title || item.name || item.label || idx} className="flex items-start gap-4 p-5">
                <div className="w-10 h-10 bg-rnb-teal/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-rnb-teal" />
                </div>
                <div>
                  <h3 className="font-bold text-rnb-navy mb-1">{item.title}</h3>
                  <p className="text-rnb-gray text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-rnb-navy to-rnb-blue relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/15 rounded-full blur-3xl" />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Don't Wait for an Audit to Find Out What's Missing
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            The best time to get your compliance in order is before you need it. Let's have a straightforward conversation about where your practice stands and what it would take to get you audit-ready.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/consultation">
              <Button
                size="lg"
                className="bg-white text-rnb-navy hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid="compliance-bottom-cta"
              >
                Book a Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/quote">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300"
              >
                Get an Instant Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComplianceCredentialing;
